import { Badge } from "@/components/ui/badge";
import type { AdminChallenge } from "@/lib/api";

/**
 * Whether the current client build can decrypt a client-simulated challenge.
 *
 * `false` on an inactive challenge is normal -- it predates the build students run
 * now, and nothing will be submitted to it. `false` on an *active* one means every
 * submission to it fails, so it is called out rather than shown as a neutral fact.
 *
 * This column has been, in turn, a "PBKDF2 | legacy" badge (said the same thing on
 * every row once the schemes merged) and then the tag itself (a secret, in a
 * browser). What it answers now is the question actually worth asking.
 */
export function ReadabilityCell({ challenge }: { challenge: AdminChallenge }) {
  if (challenge.simulator === "server") {
    // No encrypted file and no client build: the question does not apply.
    return (
      <span
        className="text-muted-foreground text-xs"
        title="Server-simulated: the maze is a library maze the worker loads, not an encrypted file."
      >
        —
      </span>
    );
  }
  const readable = challenge.readable_by_current_build;
  if (readable === null) {
    return (
      <span
        className="text-muted-foreground text-xs"
        title="The server has no COURSE_TAG configured, so this cannot be checked."
      >
        Unknown
      </span>
    );
  }
  if (readable) {
    return <span className="text-muted-foreground text-xs">Current build</span>;
  }
  return (
    <Badge
      variant={challenge.active ? "destructive" : "secondary"}
      title={
        challenge.active
          ? "Encrypted for a different vis_nav_game build than this server is configured for. Every submission to it will fail."
          : "Encrypted for an earlier vis_nav_game build. Expected for a past challenge."
      }
    >
      {challenge.active ? "Wrong build" : "Earlier build"}
    </Badge>
  );
}
